import { fetcher } from '@data/utils/api/fetcher/fetcher';
import formatDate from '@data/utils/format-date';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

// ── Response schema ────────────────────────────────────────────────────────────

export type MetricFormat = 'number' | 'percent';

const metricV2Schema = z.object({
  value: z.string().nullable(),
  label: z.string(),
  toolTip: z.string().optional(),
  dateOfAssessment: z.string().optional(),
});

export type MetricV2Response = z.infer<typeof metricV2Schema> & {
  format?: MetricFormat;
};

// ── Supported metric types ─────────────────────────────────────────────────────

export type MetricV2Type =
  | 'TOTAL_STUDENT_SESSIONS'
  | 'TOTAL_STUDENT_MINUTES'
  | 'TOTAL_MINUTES_READING'
  | 'TOTAL_LITBITS_EARNED'
  | 'LEXILE_MEASURE'
  | 'GE_SCORE'
  | 'AVG_ACTIVITY_SCORE'
  | 'ACTIVITIES_COMPLETED'
  | 'CONTENT_COMPLETIONS'
  | 'CONTENT_VIEWS'
  | 'DIGITAL_MINUTES_READ'
  | 'MANUALLY_ENTERED_MINUTES'
  | 'LEXILE_BOY_ASSESSMENTS_COMPLETE'
  | 'LEXILE_MOY_ASSESSMENTS_COMPLETE'
  | 'LEXILE_EOY_ASSESSMENTS_COMPLETE'
  | 'GE_FIRST_ASSESSMENTS_COMPLETE'
  | 'GE_LAST_ASSESSMENTS_COMPLETE';

// ── Request body ───────────────────────────────────────────────────────────────

export interface MetricV2Request {
  orgId: string;
  metricType: MetricV2Type;
  teacherIds: string[];
  startDate: Date;
  endDate: Date;
  edApps: string[];
  classIds?: string[] | null;
  studentIds?: string[] | null;
  /** Singular studentId for student-level metrics (LEXILE_MEASURE, GE_SCORE) */
  studentId?: string | null;
}

// ── Hook props ─────────────────────────────────────────────────────────────────

export interface UseMetricsV2ApiProps extends MetricV2Request {
  enabled?: boolean;
}

// ── Percent-formatted metrics ──────────────────────────────────────────────────

const PERCENT_METRICS: MetricV2Type[] = ['AVG_ACTIVITY_SCORE'];

// ── Student-level metric types (different request body shape) ───────────────────

const STUDENT_LEVEL_METRICS: MetricV2Type[] = ['LEXILE_MEASURE', 'GE_SCORE'];

// ── Fetcher ────────────────────────────────────────────────────────────────────

export const getMetricsV2Api = async ({
  orgId,
  metricType,
  teacherIds,
  startDate,
  endDate,
  edApps,
  classIds,
  studentIds,
  studentId,
}: MetricV2Request) => {
  const isStudentLevelMetric = STUDENT_LEVEL_METRICS.includes(metricType);

  const body = isStudentLevelMetric
    ? {
        orgId,
        metricType,
        studentId: studentId ?? studentIds?.[0] ?? null,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate, { boundary: 'end' }),
        edApps,
      }
    : {
        orgId,
        metricType,
        teacherIds,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate, { boundary: 'end' }),
        edApps,
        classIds: classIds ?? null,
        studentIds: studentIds ?? null,
      };

  const format: MetricFormat = PERCENT_METRICS.includes(metricType)
    ? 'percent'
    : 'number';

  return fetcher(`/metrics/${metricType}`, {
    service: '/data',
    version: '/v2',
    method: 'POST',
    body: JSON.stringify(body),
  }).then((res) => ({ ...metricV2Schema.parse(res), format }));
};

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useMetricsV2Api({
  enabled,
  ...request
}: UseMetricsV2ApiProps): UseQueryResult<MetricV2Response, Error> {
  const {
    metricType,
    orgId,
    teacherIds,
    startDate,
    endDate,
    edApps,
    classIds,
    studentIds,
  } = request;

  return useQuery({
    queryKey: [
      'metrics-v2',
      {
        metricType,
        orgId,
        teacherIds,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate, { boundary: 'end' }),
        edApps,
        classIds: classIds ?? null,
        studentIds: studentIds ?? null,
      },
    ],
    queryFn: () => getMetricsV2Api(request),
    enabled: enabled !== false,
    staleTime: 3 * 60 * 1000, // 3 Min Stale Time
    refetchOnWindowFocus: false,
  });
}
